import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { FormBuilder, UntypedFormControl } from '@angular/forms'
import {
  ApiService,
  Clinic,
  ClinicService,
  Eligibility,
  EligibilityBenefitItem,
  FileService,
  GenericCoverageItem,
  GenericDeductible,
  GenericNetworkStatus,
  PlaceOfServiceTypes,
  ServiceType,
  ServiceTypes,
  titlecase,
} from '@mmx/shared'
import { intersection, isEqual, pull } from 'lodash'
import moment from 'moment'
import { ReplaySubject, Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'mmx-eligibility-card',
  templateUrl: './eligibility-card.component.html',
  styleUrls: ['./eligibility-card.component.scss'],
  standalone: false,
})
export class EligibilityCardComponent implements OnInit, OnChanges, OnDestroy {
  @Output() estimation = new EventEmitter<Eligibility | null>()
  @Input() insuranceType: string
  @Input() placeTypes: string[] = []
  @Input() serviceCodes: string[] = []
  @Input() disabled = false
  @Input() showBenefitCard = false
  @Input() eligibility: Eligibility
  clinic: Clinic
  public PlaceOfServiceTypes = PlaceOfServiceTypes
  public planControl = new UntypedFormControl()
  public filtersForm = this.fb.group({
    filtersEnabled: this.fb.control<boolean>(true),
    serviceCodes: this.fb.control<string[]>([]),
    serviceFilterCtrl: this.fb.control<string>(null),
    placeTypes: this.fb.control<string[]>(this.placeTypes || []),
    inNetwork: this.fb.control<boolean>(false),
  })

  public filteredServiceTypes: ReplaySubject<ServiceType[]> = new ReplaySubject<
    ServiceType[]
  >(1)

  get showAll(): boolean {
    return !this.filtersForm.value.filtersEnabled
  }

  get insuranceTypeDisplay(): string {
    switch (this.insuranceType) {
      case 'P':
        return 'common.primary'
      case 'S':
        return 'common.secondary'
      case 'T':
        return 'common.tertiary'
      default:
        return ''
    }
  }

  protected onDestroy$ = new Subject<void>()

  public planFacts = [
    {
      key: 'status',
      icon: 'info',
      label: 'Plan Status',
    },
    {
      key: 'insuranceType',
      icon: 'details',
      label: 'Insurance type',
      tooltip:
        'The type of insurance, e.g. HMO, PPO, POS, COBRA, Commercial, Medicaid, Medicare Part A, Medicare Part B.',
    },
    {
      key: 'planStartDate',
      icon: 'date_range',
      label: 'Plan Start Date',
    },
    {
      key: 'planEndDate',
      icon: 'date_range',
      label: 'Plan End Date',
    },
    {
      key: 'policyEffectiveDate',
      icon: 'date_range',
      label: 'Policy Effective Date',
    },
    {
      key: 'policyExpirationDate',
      icon: 'date_range',
      label: 'Policy Expiration Date',
    },
    {
      key: 'planName',
      icon: 'description',
      label: 'Plan name',
      tooltip:
        'The product name or special program name for the insurance plan. This is often the brand or marketing name for the plan.',
    },
    {
      key: 'planNumber',
      icon: 'description',
      label: 'Plan number',
    },
    {
      key: 'coverageLevel',
      label: 'Coverage Level',
    },
    {
      key: 'policyNumber',
      icon: 'group',
      label: 'Policy number',
    },
    {
      key: 'contractNumber',
      icon: 'group',
      label: 'Contract number',
    },
    {
      key: 'networkId',
      icon: 'people',
      label: 'Network ID',
    },
    {
      key: 'networkName',
      icon: 'people_alt',
      label: 'Network name',
    },
  ]

  protected _serviceCodes: string[] = []

  get eligibilityBeginDate(): moment.Moment | undefined {
    if (this.eligibility.data) {
      if (
        this.eligibility.data.coverage &&
        this.eligibility.data.coverage.eligibility_begin_date
      ) {
        return moment(
          this.eligibility.data.coverage.eligibility_begin_date,
          'MM/DD/YYYY',
        )
      }
    }
  }

  get eligibilityEndDate(): moment.Moment | undefined {
    if (this.eligibility.data) {
      if (
        this.eligibility.data.coverage &&
        this.eligibility.data.coverage.eligibility_end_date
      ) {
        return moment(
          this.eligibility.data.coverage.eligibility_end_date,
          'MM/DD/YYYY',
        )
      }
    }
  }

  get hasErrors(): boolean {
    return (
      this.eligibility &&
      this.eligibility.data &&
      this.eligibility.data.errors &&
      this.eligibility.data.errors.length > 0
    )
  }

  ngOnInit() {
    // whenever the services are changed, reset back to show all when nothing is selected
    // and get out of show all if they select explicitly
    this.clinic = this.clinicService.clinic

    this.filtersForm.controls.filtersEnabled.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((enabled) => {
        if (enabled) {
          this.filtersForm.controls.serviceCodes.enable()
          this.filtersForm.controls.placeTypes.enable()
          this.filtersForm.controls.inNetwork.enable()
        } else {
          this.filtersForm.controls.serviceCodes.disable()
          this.filtersForm.controls.placeTypes.disable()
          this.filtersForm.controls.inNetwork.disable()
        }
      })

    this.filtersForm.controls.inNetwork.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((inNetwork) => {
        this.onInNetworkChange(inNetwork)
      })

    this.filtersForm.controls.serviceFilterCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.filterService()
      })

    if (this.disabled) {
      this.filtersForm.disable()
    }

    this.setEligibility(this.eligibility)
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.eligibility) {
      this.setEligibility(changes.eligibility.currentValue)
    }

    if (this.filtersForm) {
      if (changes.placeTypes) {
        this.filtersForm.controls.placeTypes.setValue(
          changes.placeTypes.currentValue,
        )
        this.filtersForm.controls.placeTypes.updateValueAndValidity()
      } else if (changes.serviceCodes) {
        // this.setServiceCodes()
      } else if (changes.disabled) {
        if (changes.disabled.currentValue) {
          this.filtersForm.disable()
        } else {
          this.filtersForm.enable()
        }
      }
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  setEligibility(eligibility?: Eligibility) {
    this.eligibility = eligibility

    if (this.eligibility) {
      this.filtersForm.controls.inNetwork.setValue(this.eligibility.inNetwork)
      this.filtersForm.controls.inNetwork.updateValueAndValidity()

      this.setServiceCodes()

      if (
        eligibility.data &&
        eligibility.data.plans &&
        eligibility.data.plans.length > 0
      ) {
        this.planControl.setValue(eligibility.data.plans[0])
      }

      if (this.hasErrors) {
        this.filtersForm.controls.filtersEnabled.setValue(false)
        this.filtersForm.controls.filtersEnabled.disable()
        this.filtersForm.controls.filtersEnabled.updateValueAndValidity()
      }
    }

    // if (this.filtersForm.value.serviceCodes.length === 0) {
    //   this.showAll = true
    // } else if (this.filtersForm.value.serviceCodes.length > 0) {
    //   this.showAll = false
    // }

    this.filterService()
  }

  getPlanFact(key: string): string | undefined {
    let plan = this.planControl.value

    if (
      this.eligibility &&
      this.eligibility.data &&
      this.eligibility.data.plans &&
      this.eligibility.data.plans.length === 0
    ) {
      plan = this.eligibility.data.plans[0]
    }

    if (plan) {
      const value = plan[key]
      if (
        value != null &&
        (typeof value !== 'string' || value.trim().length > 0)
      ) {
        return value
      }
    }
  }

  onInNetworkChange(inNetwork: boolean) {
    if (this.eligibility && this.eligibility.inNetwork !== inNetwork) {
      this.eligibility.inNetwork = inNetwork
      this.restService
        .updatePatientEligibility(this.eligibility)
        .subscribe(() => {
          this.estimation.emit()
        })
    }
  }

  download(event: MouseEvent) {
    const asHtml = event.altKey
    this.restService.getEligibilityPdfLink(this.eligibility.id, asHtml).subscribe({
      next: (resp) => {
        this.fileService.saveAs(resp.data)
      },
    })
  }

  inNetworkDisplay(inNetwork: GenericNetworkStatus) {
    if (inNetwork === 'yes') {
      return 'In'
    } else if (inNetwork == null || inNetwork === 'not_applicable') {
      return 'N/A'
    } else {
      return 'Out'
    }
  }

  getServiceTypes(coverageItem: any) {
    if (coverageItem.service_types) {
      const lines = coverageItem.service_types.map((type, index) => {
        const code: string = coverageItem.service_type_codes[index]
        const bold: boolean = (
          this.filtersForm.value.serviceCodes ?? []
        ).includes(code)
        type = `[${code}] ` + titlecase(type)
        return bold ? `<strong class="text-warn mat-bold">${type}</strong>` : type
      })

      return lines
    }
  }

  hasSome(coverageItems: any[]): boolean {
    return (coverageItems ?? []).some(this.shouldDisplay.bind(this))
  }

  shouldDisplay(coverageItem: EligibilityBenefitItem): boolean {
    const inNetworkFlag = coverageItem.in_plan_network || 'not_applicable'
    const isInNetwork =
      inNetworkFlag === (this.filtersForm.value.inNetwork ? 'yes' : 'no') ||
      inNetworkFlag === 'not_applicable'
    const serviceCodes = this.filtersForm.value.serviceCodes || []
    const placeTypes = this.filtersForm.value.placeTypes || []

    if (coverageItem.selected || this.showAll || this.hasErrors) {
      // if the item is selected or filters are disabled
      return true
    } else if (!isInNetwork) {
      // if the item does not have the required network status
      return false
    } else if (
      coverageItem.place_of_service != null &&
      placeTypes.length > 0 &&
      !placeTypes.includes(coverageItem.place_of_service)
    ) {
      // if the place of service doesn't match
      return false
    } else if (
      (coverageItem as GenericDeductible).time_period == null ||
      ['admission', 'visit', 'remaining'].includes(
        (coverageItem as GenericDeductible).time_period,
      ) ||
      (coverageItem as GenericDeductible).remaining_amount != null
    ) {
      // if the item has a remaining amount or is a likely time period

      // if the service codes intersect
      return serviceCodes.length === 0
        ? true
        : intersection(coverageItem.service_type_codes, serviceCodes).length > 0
    } else {
      return false
    }
  }

  hasNo(type: string) {
    return !this.eligibility.data.coverage[type].some((obj) => obj.selected)
  }

  selectCoverageType(type: string, coverageItem: GenericCoverageItem | 'none') {
    for (const obj of this.eligibility.data.coverage[type]) {
      const inNetwork = obj.in_plan_network
      const show =
        inNetwork === (this.filtersForm.value.inNetwork ? 'yes' : 'no') ||
        inNetwork === 'not_applicable'

      if (obj.selected && show) {
        delete obj.selected
      }
    }

    if (coverageItem === 'none') {
      if (this.eligibility.selectedCoverageItems == null) {
        this.eligibility.selectedCoverageItems = {}
      }
      if (this.eligibility.selectedCoverageItems[type] == null) {
        this.eligibility.selectedCoverageItems[type] = []
      }

      this.eligibility.selectedCoverageItems[type].push('none')
    } else {
      if (this.eligibility.selectedCoverageItems) {
        pull(this.eligibility.selectedCoverageItems[type], 'none')
      }

      coverageItem.selected = true

      for (const obj of this.eligibility.data.coverage[type]) {
        if (isEqual(obj, coverageItem)) {
          obj.selected = true
          break
        }
      }
    }

    this.restService
      .updatePatientEligibility(this.eligibility)
      .subscribe(() => {
        // Update estimation card
        this.estimation.emit() // { type, coverageItem })
      })
  }

  openRawResponse() {
    this.restService
      .getEligibility(this.eligibility.id, true)
      .subscribe((response) => {
        const newWindow = window.open()
        if (newWindow) {
          const html = `<pre><code>${JSON.stringify(
            {
              id: response.id,
              request: response.payload,
              response: response.raw,
              parsed: response.data,
            },
            null,
            2,
          )}</code></pre>`
          newWindow.document.write(html)
        } else {
          alert('A popup blocker prevented us from opening the raw response')
        }
      })
  }

  protected filterService() {
    let serviceTypesIncludedInEligibility = this.eligibility
      ? ServiceTypes.filter((serviceType) => {
        if (this.eligibility.data.service_type_codes) {
          return this.eligibility.data.service_type_codes.includes(
            serviceType.code,
          )
        }
      })
      : []
    if (serviceTypesIncludedInEligibility.length === 0) {
      serviceTypesIncludedInEligibility = ServiceTypes.slice()
    }
    let search = this.filtersForm.value.serviceFilterCtrl
    if (!search) {
      this.filteredServiceTypes.next(serviceTypesIncludedInEligibility)
    } else {
      search = search.toLowerCase()
      const services = serviceTypesIncludedInEligibility.filter((service) => {
        return (
          service.code.indexOf(search) > -1 ||
          service.name.toLowerCase().indexOf(search) > -1
        )
      })
      this.filteredServiceTypes.next(services)
    }
  }

  protected setServiceCodes() {
    if (
      this.serviceCodes != null &&
      this.eligibility != null &&
      this.eligibility.data != null &&
      this.eligibility.data.service_type_codes &&
      this.eligibility.data.service_type_codes.length > 0
    ) {
      const serviceCodes = this.serviceCodes.filter((code) => {
        return (
          this.eligibility.data.service_type_codes &&
          this.eligibility.data.service_type_codes.includes(code)
        )
      })

      this.filtersForm.controls.serviceCodes.setValue(serviceCodes)
      this.filtersForm.controls.serviceCodes.updateValueAndValidity()
    }
  }

  constructor(
    private clinicService: ClinicService,
    private restService: ApiService,
    private fileService: FileService,
    private fb: FormBuilder,
  ) { }

  goToCommonEligibilityErrorsArticle() {
    window.open('https://help.patientpal.com/en/articles/5506484-common-eligibility-errors', '_blank')
  }
}
